/**
 * Fetch Utility
 * Provides a robust wrapper around the fetch API with retry logic, timeout, and error handling.
 */

/**
 * Fetch with retry logic for robust API calls
 * @param {string} url - The URL to fetch
 * @param {object} options - Fetch options (method, headers, body, etc.)
 * @param {number} retries - Number of retries (default 3)
 * @param {number} backoff - Initial backoff delay in ms (default 1000)
 * @param {number} timeout - Request timeout in ms (default 10000)
 * @returns {Promise<Response>}
 */
function fetchWithRetry(url, options = {}, retries = 3, backoff = 1000, timeout = 10000) {
  const startTime = performance.now();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  // Merge signal with options
  const fetchOptions = {
    ...options,
    signal: options.signal || controller.signal
  };

  return fetch(url, fetchOptions)
    .then(response => {
      clearTimeout(timeoutId);
      const duration = performance.now() - startTime;

      // Monitor API response time
      if (window.performance && window.performance.mark) {
        const sanitizedUrl = url.split('?')[0];
        try {
          performance.measure(`api-call:${sanitizedUrl}`, { start: startTime, duration });
        } catch (e) {
          // Ignore performance measure errors
        }
        
        const logData = {
          url: sanitizedUrl,
          duration: duration.toFixed(2),
          status: response.status,
          timestamp: new Date().toISOString()
        };
        
        console.debug(`[Fetch] Success:`, logData);
      }

      // If response is successful (2xx), return it
      if (response.ok) {
        return response;
      }

      // Handle 401 Unauthorized specifically
      if (response.status === 401) {
        console.error('[Fetch] Unauthorized (401). Session may have expired or request is invalid.', {
          url,
          timestamp: new Date().toISOString()
        });
        // Use window.document if available (for browser environment)
        if (typeof document !== 'undefined') {
          document.dispatchEvent(new CustomEvent('ajax:auth:error', { detail: { url, status: 401 } }));
        }
        throw new Error(`Unauthorized request: ${response.status}`);
      }

      // If status is 429 (Too Many Requests) or 5xx (Server Error), we retry
      if (retries > 0 && (response.status === 429 || response.status >= 500)) {
        console.warn(`[Fetch] Request failed with status ${response.status}. Retrying in ${backoff}ms... (${retries} attempts left)`);
        
        return new Promise(resolve => setTimeout(resolve, backoff))
          .then(() => fetchWithRetry(url, options, retries - 1, backoff * 2, timeout));
      }

      // For client errors (400-499) that are not 401/429, return response so caller can parse JSON error body
      if (response.status >= 400 && response.status < 500) {
        return response;
      }

      // For other errors (unexpected), throw immediately
      throw new Error(`Request failed with status ${response.status}`);
    })
    .catch(error => {
      clearTimeout(timeoutId);
      
      // Check if error is 401, if so do not retry
      if (error.message && (error.message.includes('Unauthorized') || error.message.includes('401'))) {
         throw error;
      }

      const isAbort = error.name === 'AbortError';
      const errorMessage = isAbort ? 'Request timed out' : error.message;

      if (retries > 0) {
        console.warn(`[Fetch] Error: ${errorMessage}. Retrying in ${backoff}ms... (${retries} attempts left)`);
        return new Promise(resolve => setTimeout(resolve, backoff))
          .then(() => fetchWithRetry(url, options, retries - 1, backoff * 2, timeout));
      }
      
      // Final error logging
      console.error(`[Fetch] Permanent failure for ${url} after retries.`, error);
      throw error;
    });
}

// Expose globally
window.fetchWithRetry = fetchWithRetry;
